import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useRecoilValue } from 'recoil';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { getCoverableIndex } from '../../../../utils/coverablesUtil';
import { Grid, GridItem, LinkBox, LoadingDots } from '@flos/react-ui';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import CarInfoBox from './CarInfoBox';
import { isRequestPendingAtom } from '../../../../recoil/atoms/isRequestPendingAtom';
import { track } from '../../../../utils/trackingUtil';
import ViewInformationModal from '../../../../shared_components/ViewInformationModal/ViewInformationModal';
import { useState } from 'react';
import { carTrackingRequirementSelector } from '../../../../recoil/selectors/carTrackingRequirementSelector';

const BoughtOrLeasedStep = ({ goNext }: { goNext: () => void }): React.JSX.Element => {
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const getHippoText = useHippoText();
    const { cars = [] } = coverables;
    const coverableIndex = getCoverableIndex(cars, currentProduct.fixedId);
    const coverable = cars[coverableIndex];
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const isTrackingRequired = useRecoilValue(carTrackingRequirementSelector);
    const [showTrackingRequiredModal, setShowTrackingRequiredModal] = useState(isTrackingRequired);
    const [isLoading, setIsLoading] = useState(false);

    const handleClick = (choice: boolean): void => {
        if (isLoading || isRequestPending) return;
        setIsLoading(true);
        updateCoverableInformation({
            isInLeasing: choice
        });

        const updatedCoverable: CoverableInformationToUpdateDTO = {
            car: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                isInLeasing: choice
            },
            productToUpdate: currentProduct.flavourName
        };
        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`${currentProduct.flavourName}ContentCoverableInformationBoughtOrLeased`, [
                {
                    isInLeasing: choice
                }
            ]);
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate hidePrevious hideNext>
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-HEADER')}
            >
                <Grid>
                    <GridItem colStart={1} span={8}>
                        <div className="u-display-flex u-flex-column u-flex-items-start u-gap-5">
                            <LinkBox
                                className={
                                    isLoading
                                        ? 'u-color-supplement-grey-medium flos-box'
                                        : 'flos-box'
                                }
                                title={getHippoText(
                                    'DSS-COVERABLE-INFORMATION-STEP-LEASING-BOUGHT'
                                )}
                                iconShape="money-bag-kroner"
                                onClick={() => {
                                    handleClick(false);
                                }}
                                arrowArea={
                                    (isLoading || isRequestPending) && !coverable.isInLeasing ? (
                                        <div className="u-spacing-stack-bottom-6 u-spacing-inset-right-6">
                                            <LoadingDots centered={false} />
                                        </div>
                                    ) : undefined
                                }
                            />
                            <LinkBox
                                className={
                                    isLoading
                                        ? 'u-color-supplement-grey-medium flos-box'
                                        : 'flos-box'
                                }
                                title={getHippoText(
                                    'DSS-COVERABLE-INFORMATION-STEP-LEASING-LEASED'
                                )}
                                iconShape="document-sign"
                                onClick={() => {
                                    handleClick(true);
                                }}
                                arrowArea={
                                    (isLoading || isRequestPending) && coverable.isInLeasing ? (
                                        <div className="u-spacing-stack-bottom-6 u-spacing-inset-right-6">
                                            <LoadingDots centered={false} />
                                        </div>
                                    ) : undefined
                                }
                            />
                        </div>
                    </GridItem>
                    <GridItem span={4}>
                        <CarInfoBox isVehicleFound={true} coverable={coverable} />
                    </GridItem>
                </Grid>
            </CoverableDetailsTemplate>
            {showTrackingRequiredModal && (
                <ViewInformationModal
                    closeModal={() => {
                        setShowTrackingRequiredModal(false);
                    }}
                    showModal={showTrackingRequiredModal}
                    modalProps={{
                        title: getHippoText('DSS-INFO-MODAL-CAR-TRACKING-REQUIREMENT-TITLE'),
                        description: getHippoText(
                            'DSS-INFO-MODAL-CAR-TRACKING-REQUIREMENT-DESCRIPTION'
                        )
                    }}
                />
            )}
        </WizardPagetemplate>
    );
};

export default BoughtOrLeasedStep;
